import { Add as AddIcon } from '@clearkit/icons';
import { CKBox, CKButton } from 'clearkit';
import { ElementType, Fragment, ReactNode } from 'react';
import { Link } from 'react-router-dom';

type PanelProps = {
  children: ReactNode;
};
export function Root(props: PanelProps) {
  return <CKBox className="" {...props} />;
}

export function Header(props: PanelProps) {
  return <div className="px-4 pt-4 mb-8 lg:flex gap-6 lg:mb-2" {...props} />;
}
export function HeaderContent(props: PanelProps) {
  return <div className="flex-grow mb-2" {...props} />;
}

export function Title(props: PanelProps) {
  return <span className="block text-base font-medium" {...props} />;
}

export function Subtitle(props: PanelProps) {
  return <span className="text-sm text-gray-600" {...props} />;
}

type PanelCTAProps = {
  href: string | { pathname: string };
  children: ReactNode;
};
export function CTA({ href, children }: PanelCTAProps) {
  return (
    <div className="text-right min-w-48">
      <CKButton as={Link} to={href} variant="bold" variantColor="blue">
        {children}
      </CKButton>
    </div>
  );
}

export function Separator() {
  return <hr className="w-full border-0 border-t border-gray-200" />;
}

export function Body(props: PanelProps) {
  return (
    <div
      className="px-4 mt-4 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4"
      {...props}
    />
  );
}

export type Destination = {
  name: string;
  description: string;
  icon: ReactNode;
};
type DestinationsProps = {
  destinations: Destination[];
};
export function Destinations({ destinations }: DestinationsProps) {
  return (
    <>
      {destinations.map((destination) => (
        <div className="flex mb-4" key={destination.name}>
          {destination.icon}
          <div className="flex flex-col flex-grow">
            <span className="text-base text-medium">{destination.name}</span>
            <span className="text-sm text-gray-600">
              {destination.description}
            </span>
          </div>
        </div>
      ))}
    </>
  );
}

type FooterProps = FooterIconProps & {
  description: ReactNode;
};
export function Footer({ icons, description }: FooterProps) {
  if (!icons?.length) {
    return null;
  }

  return (
    <>
      <Separator />
      <div className="items-center px-4 mt-2 mb-2 text-center lg:text-left lg:flex">
        <FooterIcons icons={icons} />
        <FooterDescription>{description}</FooterDescription>
      </div>
    </>
  );
}

export type FooterIcon = ElementType;
type FooterIconProps = {
  icons?: FooterIcon[];
};
export function FooterIcons({ icons }: FooterIconProps) {
  return (
    <div className="flex justify-center py-3 lg:py-0 lg:justify-start">
      {icons?.map((Icon, index: number) => (
        <Fragment key={index}>
          {index !== 0 && <AddIcon className="mr-1 text-gray-500" />}
          <Icon className="w-4 h-4 mr-1" />
        </Fragment>
      ))}
    </div>
  );
}

export function FooterDescription(props: PanelProps) {
  return (
    <div className="w-full pb-3 ml-1 text-sm lg:w-auto lg:pb-0" {...props} />
  );
}
